/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';

import { ClientApiProps } from '../../common';
import { Button, DataGrid, DataGridColumn, Header, TextInput, mergeRows } from 'acma-spa';
import { Acma_amateurradioexamresults, Acma_amateurradioexamstatuses, AssessmentSorting, Client, IAssessment, getApiClient } from '../../api';
import { AssessmentDetails } from './AssessmentDetails';
import InfiniteScroll from 'react-infinite-scroll-component';

export interface AssessmentsProps extends ClientApiProps {
  isAssessor: boolean;
}

export const Assessments: React.FC<AssessmentsProps> = (props) => {
  const [rows, setRows] = useState<IAssessment[] | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState<string>('');
  const [pageNumber, setPageNumber] = useState(1);
  const [sorting, setSorting] = useState({ columnIndex: 4, descending: true });
  const [expanded, setExpanded] = useState<(IAssessment | null)[]>([]);
  const pageSize = 1000;

  const load = async (client: Client) => {
    if (pageNumber == 1) {
      setRows(null);
      // console.log("Loading...")
    }

    try {
      const sortColumnList = [
        AssessmentSorting.Acma_Name,
        AssessmentSorting.EmailAddress,
        AssessmentSorting.Acma_Type,
        AssessmentSorting.Acma_Score,
        AssessmentSorting.CreatedOn,
      ];
      let sortColumn = AssessmentSorting.CreatedOn;
      let sortDesc = true;
      if (sorting.columnIndex >= 0 && sorting.columnIndex < sortColumnList.length) {
        sortColumn = sortColumnList[sorting.columnIndex];
        sortDesc = sorting.descending;
      }
      const newRows = await client.assessmentsGet(sortColumn, sortDesc, search, pageNumber, pageSize);
      if (newRows.length < pageSize) setHasMore(false);

      pageNumber == 1 ? setRows(newRows) : setRows(rows?.concat(newRows) ?? newRows);
    } catch (e) {
      if (!rows) setRows([]);
    }
  };

  useEffect(() => {
    if (!props.isAssessor) {
      return;
    }
    const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);

    load(client);

    return function () {
      abort.abort();
    };
  }, [props.isAssessor, search, pageNumber, sorting]);

  const isPassed = (r: IAssessment) => {
    return (
      ((r.practicalComponent == Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision &&
        r.practicalResult == Acma_amateurradioexamresults.Competent) ||
        r.practicalComponent == Acma_amateurradioexamstatuses.ExaminationPassedInThePast ||
        r.practicalComponent == Acma_amateurradioexamstatuses.PassedUnderPriorLearningRecognition) &&
      ((r.theoreticalComponent == Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision && (r.theoreticalScore ?? 0) >= 70) ||
        r.theoreticalComponent == Acma_amateurradioexamstatuses.ExaminationPassedInThePast ||
        r.theoreticalComponent == Acma_amateurradioexamstatuses.PassedUnderPriorLearningRecognition) &&
      ((r.regulatoryComponent == Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision && (r.regulatoryScore ?? 0) >= 70) ||
        r.regulatoryComponent == Acma_amateurradioexamstatuses.ExaminationPassedInThePast ||
        r.regulatoryComponent == Acma_amateurradioexamstatuses.PassedUnderPriorLearningRecognition)
    );
  };

  const columns: DataGridColumn<IAssessment>[] = [
    { label: 'Name', value: (r) => [r.firstName, r.lastName, r.otherName].filter((r) => r && r.length > 0).join(' ') },
    { label: 'Email', value: (r) => r.emailAddress },
    { label: 'Qualification', value: (r) => r.type },
    { label: 'Outcome', value: (r) => (isPassed(r) ? 'Pass' : 'Fail') },
    { label: 'Created On', value: (r) => r.createdOn?.toLocaleDateString() },
  ];

  const onSort = (columnIndex: number, descending: boolean) => {
    if (columnIndex < 0) {
      columnIndex = sorting.columnIndex;
    }
    setSorting({ columnIndex, descending });
    setPageNumber(1);
  };

  const onSearch = (v: string) => {
    setSearch(v);
    setPageNumber(1);
  };

  const onPaginate = () => {
    if (rows && rows.length > 0) {
      setPageNumber(pageNumber + 1);
    }
  };

  const onChange = (current: IAssessment | null, update: IAssessment | null) => {
    const newRows = mergeRows(rows ?? [], current, update);

    if (sorting.columnIndex >= 0) {
      newRows.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        if (sorting.columnIndex == 4) {
          aVal = a.createdOn;
          bVal = b.createdOn;
        } else {
          aVal = columns[sorting.columnIndex].value(a) as any;
          bVal = columns[sorting.columnIndex].value(b) as any;
        }

        if (aVal > bVal) {
          return 1 * (sorting.descending ? -1 : 1);
        } else if (aVal < bVal) {
          return -1 * (sorting.descending ? -1 : 1);
        } else {
          return 0;
        }
      });
    }

    setRows(newRows);
    setExpanded(expanded.filter((r) => r != current));
  };

  return (
    <>
      <Header label="Examination results" />

      <div className="row my-2">
        <div className="col">
          {/* <TextInput id="search-assessor" value={search} onChange={onSearch} onEnter={onSearch} placeholder="Search..." size="xl" /> */}
        </div>
        <div className="col col-auto">
          <Button label="Add" id="add-assessor" onClick={() => setExpanded([null])} />
        </div>
      </div>

      <InfiniteScroll
        dataLength={rows?.length ?? 0} //This is important field to render the next data
        next={onPaginate}
        hasMore={hasMore}
        loader={<h4>Loading...</h4>}>
        <DataGrid<IAssessment>
          columns={columns}
          rows={rows ?? []}
          noRowsMessage={rows == null ? 'Loading...' : 'No exams added yet'}
          sort={{
            columnIndex: sorting.columnIndex,
            descending: sorting.descending,
            onChange: onSort,
          }}
          expand={{
            rows: expanded,
            onChange: setExpanded,
            content: (current) => <AssessmentDetails {...props} row={current} onChange={(update) => onChange(current, update)} />,
          }}
        />
      </InfiniteScroll>
    </>
  );
};
